import React, { useEffect, useState, useRef } from "react";
import LanguageContext from "../../../helper/LanguageContext";
import { DE } from "../../../Languages/DE";
import { EN } from "../../../Languages/EN";
import "./DepartLocaDialogue.css";

export default function DepartLocaDialogue({ articleType, handleClose, setParam, positions, setSelectName, setDepName, multiple, onSelectMultiple, anchorElement, showDepartModal }) {
    const { isGerm, strings } = React.useContext(LanguageContext)
    const [myPosition, setMyPositions] = useState([])
    const [modalPosition, setModalPosition] = useState(null)
    const modalRef = useRef(null)
    
    // Determine if we're in "modal mode" (no anchorElement) or "dropdown mode" (with anchorElement)
    const isModalMode = !anchorElement

    // Callers rebuild `positions` inline on every render; resync only when the
    // list or its preselection actually changes, otherwise a parent re-render
    // (e.g. editor blur on clicking Confirm) wipes the unconfirmed selection.
    const positionsKey = (positions || []).map(p => `${p.id}:${p.isSelected ? 1 : 0}`).join(',')
    useEffect(() => {
        setMyPositions(positions || [])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionsKey])

    useEffect(() => {
        // In modal mode, we don't need to calculate position (the parent handles centering)
        if (isModalMode) {
            // Set a dummy position to allow rendering
            setModalPosition({ modal: true })
            return
        }
        
        // Position dropdown relative to anchor element - only calculate when both are available
        if (anchorElement && showDepartModal) {
            const updatePosition = () => {
                const rect = anchorElement.getBoundingClientRect()
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop
                const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
                
                setModalPosition({
                    top: rect.bottom + scrollTop + 8,
                    left: rect.left + scrollLeft,
                    width: rect.width
                })
            }

            // Calculate position immediately
            updatePosition()

            // Recalculate on scroll/resize
            window.addEventListener('scroll', updatePosition, true)
            window.addEventListener('resize', updatePosition)

            return () => {
                window.removeEventListener('scroll', updatePosition, true)
                window.removeEventListener('resize', updatePosition)
            }
        } else {
            setModalPosition(null)
        }
    }, [anchorElement, showDepartModal, isModalMode])

    // Close dropdown when clicking outside (only in dropdown mode)
    useEffect(() => {
        if (isModalMode || !showDepartModal) return

        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target) && 
                anchorElement && !anchorElement.contains(event.target)) {
                handleClose()
            }
        }

        // Use setTimeout to avoid immediate trigger
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside)
        }, 0)

        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [anchorElement, showDepartModal, handleClose, isModalMode])

    const handleItemClick = (cform) => {
        const displayName = (cform.location_name && cform.location_name !== 'null') 
            ? `${cform.department_name}/${cform.location_name}` 
            : `${cform.department_name}`;
        
        if (multiple && onSelectMultiple) {
            // Multi-select mode: toggle selection locally (applied on confirm)
            setMyPositions(prev => prev.map(p => 
                p.id === cform.id ? { ...p, isSelected: !p.isSelected } : p
            ))
        } else {
            // Single-select mode
            if (setParam) setParam('deptLocation', cform.id)
            if (setDepName) setDepName(displayName)
            if (setSelectName) setSelectName(cform)
            handleClose()
        }
    }

    const handleConfirm = () => {
        if (multiple && onSelectMultiple) {
            const selectedItems = myPosition.filter(p => p.isSelected)
            const ids = selectedItems.map(p => p.id)
            const names = selectedItems.map(p => {
                return (p.location_name && p.location_name !== 'null') 
                    ? `${p.department_name}/${p.location_name}` 
                    : p.department_name
            }).join(', ')
            onSelectMultiple(ids, names)
        }
        handleClose()
    }

    const selectedCount = multiple ? myPosition.filter(p => p.isSelected).length : 0
    const allSelected = multiple && myPosition.length > 0 && selectedCount === myPosition.length

    const handleToggleAll = () => {
        setMyPositions(prev => prev.map(p => ({ ...p, isSelected: !allSelected })))
    }

    const isInternal = articleType == 1

    // Don't render if position is not calculated yet (only applies in dropdown mode)
    if (!modalPosition) {
        return null
    }
    
    // In dropdown mode, also require showDepartModal to be true
    if (!isModalMode && !showDepartModal) {
        return null
    }

    // Mode-specific layout lives in DepartLocaDialogue.css; dropdown mode is
    // positioned inline from the measured anchor position.
    const containerStyle = isModalMode 
        ? undefined
        : {
            // Dropdown mode: positioned relative to anchor
            top: `${modalPosition.top}px`,
            left: `${modalPosition.left}px`,
            width: `${modalPosition.width}px`, 
        }

    return (
        <div 
            ref={modalRef}
            className={`dept-loc-modal-container depart-loca ${isModalMode ? "depart-loca--modal" : "depart-loca--dropdown"}${isInternal ? " depart-loca--internal" : ""}`}
            style={containerStyle}
        >
            {/* Header */}
            <div className="dept-loc-modal-header depart-loca__header">
                <h5 className="depart-loca__title">{strings.dep_loc}</h5>
                <button 
                    onClick={handleClose} 
                    className={`close-article-btn kj-btn kj-btn--icon kj-btn--neutral depart-loca__close${isInternal ? " kj-btn--internal" : ""}`}
                    data-article-type={articleType}
                >
                    <span className="material-symbols-outlined close-icon">close</span>
                </button>
            </div>

            {/* Select All — multi-select only */}
            {multiple && myPosition.length > 1 && (
                <div
                    onClick={handleToggleAll}
                    className={`depart-loca__select-all${allSelected ? " depart-loca__select-all--active" : ""}`}
                >
                    <span className="depart-loca__select-all-label">
                        {allSelected ? (strings.deselect_all || 'Deselect All') : (strings.select_all || 'Select All')}
                    </span>
                    {allSelected && (
                        <span className="material-symbols-outlined depart-loca__check">check</span>
                    )}
                </div>
            )}

            {/* Body */}
            <div className="dept-loc-modal-scroll depart-loca__body">
                <div className="d-flex flex-column w-100">
                    {myPosition.map((cform) => {
                        const displayName = (cform.location_name && cform.location_name !== 'null') 
                            ? `${cform.department_name}/${cform.location_name}` 
                            : `${cform.department_name}`;
                        const isSelected = cform.isSelected
                        const selectedBg = isInternal ? "rgba(var(--kjuup-internal-rgb), 0.15)" : "rgba(var(--kjuup-accent-rgb), 0.15)"
                        const hoverBg = isInternal ? "rgba(var(--kjuup-internal-rgb), 0.08)" : "rgba(var(--kjuup-accent-rgb), 0.08)"
                        
                        return (
                            <div 
                                key={cform.id} 
                                onClick={() => handleItemClick(cform)}
                                className="dept-loc-item depart-loca__item"
                                style={{ backgroundColor: isSelected ? selectedBg : "#fff" }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = hoverBg
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = isSelected ? selectedBg : "#fff"
                                }}
                            >
                                <span className="depart-loca__item-name">
                                    {displayName}
                                </span>
                                {multiple && isSelected && (
                                    <span className="material-symbols-outlined depart-loca__check">check</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Confirm button — only in multi-select mode */}
            {multiple && (
                <div className="depart-loca__footer">
                    <button
                        onClick={handleConfirm}
                        disabled={selectedCount === 0}
                        className={`kj-btn kj-btn--primary kj-btn--block${isInternal ? " kj-btn--internal" : ""}`}
                    >
                        {strings.confirm || 'Confirm'}{selectedCount > 0 ? ` (${selectedCount})` : ''}
                    </button>
                </div>
            )}
        </div>
    )
}
